import { useMemo, useState } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  DataTable,
  HStack,
  NoDataMessage,
  Pagination,
  TextField,
  VStack,
} from '@rtkwlf/fenrir-react';
import { faSearch } from '@rtkwlf/pro-solid-svg-icons/faSearch';
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { GeneralWrapper } from '@packages/components/generalWrapper/GeneralWrapper';
import { Alert } from '@packages/fenrir/components';

import { OPTIMISTIC_UPDATE_PREFIX } from '../constants';
import { useScannerScanCredentialsWrapper } from '../use-scanner-scan-credentials-wrapper/use-scanner-scan-credentials-wrapper';

import { useCredentialedScanningColumns } from './credentialed-scanning-columns/credentialed-scanning-columns';

const SKELETON_ROWS = 2;

export const CredentialedScanningTable = () => {
  const { t } = useTranslation('telemetryScannerConfig');
  const { scannerId = '' } = useParams();
  const [globalFilter, setGlobalFilter] = useState<string>('');
  const { data, isError, isFetching } =
    useScannerScanCredentialsWrapper(scannerId);

  const showProcessingInfoBanner = useMemo(() => {
    if (data) {
      return data.some((row) => row.id.includes(OPTIMISTIC_UPDATE_PREFIX));
    }
    return false;
  }, [data]);

  const table = useReactTable({
    columns: useCredentialedScanningColumns(),
    data: data ?? [],
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      globalFilter,
    },
  });

  if (table.getState().sorting.length === 0) {
    table.setSorting([
      {
        desc: false,
        id: 'actions',
      },
    ]);
  }

  const totalMatchedRecords = table.getPrePaginationRowModel().rows.length;

  return (
    <GeneralWrapper isError={isError} width="full">
      {showProcessingInfoBanner && (
        <Alert variant="standard" severity="info">
          {t('credentialedScanning.processingInfoBannerText')}
        </Alert>
      )}
      <VStack
        gap="default"
        width="full"
        alignItems="flex-end"
        marginTop="medium"
        data-testid="credential-scanning-table"
      >
        {!isError && (
          <HStack alignSelf="flex-start">
            <TextField
              type="text"
              placeholder={t('components:filter.placeholder.search')}
              fontSize="default"
              fontWeight="normal"
              iconRight={<FontAwesomeIcon icon={faSearch} />}
              onChange={(e) => {
                setGlobalFilter(e.target.value as string);
              }}
            />
          </HStack>
        )}

        <DataTable
          table={table}
          skeletonRows={isFetching ? SKELETON_ROWS : undefined}
          stickyLastColumn
        />

        {!isFetching && table.getFilteredRowModel().flatRows.length === 0 && (
          <VStack xAlign="center" width="full" paddingY="default">
            <NoDataMessage table={table} />
          </VStack>
        )}

        {totalMatchedRecords > 0 && (
          <Pagination table={table} totalRows={totalMatchedRecords} />
        )}
      </VStack>
    </GeneralWrapper>
  );
};
